import { useCallback } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import { Features } from '~/components/FeatureToggle';
import { Modality } from '~/domain/dicom/modality';
import { Image3D } from '~/domain/patient/image3d';
import { StructureSet } from '~/domain/patient/structureset';
import { useComparisonContouring } from '~/hooks/workflows/backend/tasks/useComparisonContouring';
import { useComparisonContourModel } from '~/hooks/workflows/backend/tasks/useComparisonContourModel';
import { useDetachedContourRun } from '~/hooks/workflows/backend/tasks/useDetachedContourRun';
import {
    QaRunPhase,
    replanningQaRunState,
    replanningRunOutputsState,
} from '~/state/atoms/adaptiveReplanningAtoms';
import { useRunScans } from './useRunScans';
import AppConfig from '~/utils/appconfig';

export interface RegistrationQaRun {
    readonly phase: QaRunPhase;
    readonly blockedReason?: string;
    // Whether this run has been measured, or its measurement started.
    readonly attempted: boolean;
    // Both scans' Contour+ results are in, so the measurement can start without opening the step.
    readonly contoursReady: boolean;
    readonly calculate: () => void;
    // Contour+ on the planning scan, to be deformed onto the target.
    readonly sourceSet?: StructureSet;
    // Contour+ on the target scan: the reference the deformed set is measured against.
    readonly targetSet?: StructureSet;
    // The run's Contour+ on each scan, ready to display: the exact structures the measurement compares.
    readonly planningContourSet?: StructureSet;
    readonly targetContourSet?: StructureSet;
}

export function useRegistrationQaRun(): RegistrationQaRun {
    const { planning: planningImage, target: targetImage } = useRunScans();
    const outputs = useRecoilValue(replanningRunOutputsState);
    // The model the run's Contour+ structures came from, whatever is chosen since.
    const model = useComparisonContourModel(outputs.contourModel);
    const { start: contourScan } = useComparisonContouring(
        outputs.contourModel
    );
    const [state, setState] = useRecoilState(replanningQaRunState);
    const runTarget = useDetachedContourRun(outputs.contourJobId);
    const runSource = useDetachedContourRun(outputs.contourSourceJobId);
    const source = useDetachedContourRun(state.jobIds.source);
    const target = useDetachedContourRun(state.jobIds.target);

    const measuresCurrentRun =
        !!outputs.propagatedUid && state.forRun === outputs.propagatedUid;
    // A canceled scan waits for the user to measure again rather than failing the measurement.
    const phase: QaRunPhase =
        !measuresCurrentRun || state.phase === 'idle'
            ? 'idle'
            : source.kind === 'cancelled' || target.kind === 'cancelled'
              ? 'cancelled'
              : state.phase === 'failed' ||
                  source.kind === 'failed' ||
                  target.kind === 'failed'
                ? 'failed'
                : source.kind === 'ready' && target.kind === 'ready'
                  ? 'ready'
                  : 'segmenting';

    // Measured by the Verify service: without it the result could never arrive.
    const blockedReason = !AppConfig.isFeatureEnabled(Features.SEG_VERIFY)
        ? 'replanning.qa.blocked.noVerify'
        : !outputs.propagatedUid
          ? 'replanning.qa.blocked.noRun'
          : !model
            ? 'replanning.qa.blocked.noModel'
            : !planningImage || !targetImage
              ? 'replanning.qa.blocked.noScans'
              : planningImage.firstSlice?.modality !== Modality.CT
                ? 'replanning.qa.blocked.modality'
                : undefined;

    const segment = useCallback(
        (image: Image3D, half: 'source' | 'target', forRun: string) => {
            const settle = (jobId: string | undefined) =>
                setState((current) =>
                    current.forRun !== forRun
                        ? current
                        : jobId
                          ? {
                                ...current,
                                jobIds: { ...current.jobIds, [half]: jobId },
                            }
                          : { ...current, phase: 'failed' }
                );
            contourScan(image).then(settle, () => settle(undefined));
        },
        [contourScan, setState]
    );

    const calculate = useCallback(() => {
        const forRun = outputs.propagatedUid;
        if (
            blockedReason ||
            !forRun ||
            !planningImage ||
            !targetImage ||
            phase === 'segmenting'
        ) {
            return;
        }
        // Propagate already contoured both scans; only a scan whose job failed is contoured again.
        const reused = (run: typeof runTarget, jobId: string | undefined) =>
            run.kind === 'ready' || run.kind === 'pending' ? jobId : undefined;
        const reusedSource = reused(runSource, outputs.contourSourceJobId);
        const reusedTarget = reused(runTarget, outputs.contourJobId);
        setState({
            phase: 'segmenting',
            jobIds: { source: reusedSource, target: reusedTarget },
            forRun,
        });
        if (!reusedSource) {
            segment(planningImage, 'source', forRun);
        }
        if (!reusedTarget) {
            segment(targetImage, 'target', forRun);
        }
    }, [
        blockedReason,
        phase,
        planningImage,
        targetImage,
        runSource,
        runTarget,
        outputs.propagatedUid,
        outputs.contourJobId,
        outputs.contourSourceJobId,
        segment,
        setState,
    ]);

    return {
        phase,
        blockedReason,
        attempted: measuresCurrentRun,
        contoursReady: runSource.kind === 'ready' && runTarget.kind === 'ready',
        calculate,
        sourceSet:
            phase === 'ready' && source.kind === 'ready'
                ? source.structureSet
                : undefined,
        targetSet:
            phase === 'ready' && target.kind === 'ready'
                ? target.structureSet
                : undefined,
        planningContourSet:
            runSource.kind === 'ready' ? runSource.structureSet : undefined,
        targetContourSet:
            runTarget.kind === 'ready' ? runTarget.structureSet : undefined,
    };
}
